import { BaseToolbarProps, Tool } from './BaseToolbar.types';
import { ToolOptionsProps } from './toolbar-options/tool-options-renderer/ToolOptionsRenderer.types';
import { useCallback, useMemo, useState } from 'react';
import { useStore } from 'src/contexts/StoreContext';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { configurationTestID } from 'src/helpers/testIDs.helper';
import { LookupCodes } from 'src/constants/lookup.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { ToolbarTileProps } from './toolbar-tile/ToolbarTile';
import { ToolbarOptionsProps } from './toolbar-options/ToolbarOptions';
import { BaseToolbarIconNamesMap } from './BaseToolbar.constants';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { logError } from 'src/_shared/logger';

export const useBaseToolbarLogic = ({
  tools,
  isVisible = true,
  tilesExpandAnimationEnabled = false,
  initialSelectedToolCode,
  selectedToolCode,
  setSelectedToolCode,
  isV3,
}: BaseToolbarProps) => {
  const {
    i18n,
    tutorial: { setTutorialArea },
    project: { activeGroup, activeProject },
    toastStore: { showOnDuplicateToast },
    dynamicModal: { openModal },
  } = useStore();
  const [areTilesExpanded, setAreTilesExpanded] = useState(!tilesExpandAnimationEnabled);

  const selectedToolProps =
    typeof selectedToolCode === 'string' && tools.find(tool => tool.tool.code === selectedToolCode);
  const isToolbarOptionsOpened =
    selectedToolProps &&
    typeof selectedToolProps.tool.choices !== 'undefined' &&
    typeof selectedToolProps.ToolOptionsRenderer !== 'undefined';

  const closeToolbarOptions = useCallback(() => {
    if (isToolbarOptionsOpened && !selectedToolProps.isLookup) {
      setSelectedToolCode(initialSelectedToolCode);
    }
  }, [isToolbarOptionsOpened]);

  const closeToolbar = useCallback(() => {
    if (isToolbarOptionsOpened && !selectedToolProps.isLookup) {
      setSelectedToolCode(initialSelectedToolCode);
    } else if (isV3) {
      activeGroup?.deselectAllProducts();
    }
  }, [activeGroup, isToolbarOptionsOpened]);

  const { tiles, options } = useMemo<{ tiles: ToolbarTileProps[]; options: ToolbarOptionsProps[] }>(() => {
    return tools.reduce<{ tiles: ToolbarTileProps[]; options: ToolbarOptionsProps[] }>(
      (acc, toolProps: Tool<ToolOptionsProps>, toolIndex: number) => {
        const { tool, ToolOptionsRenderer, onOpenTool, onCloseTool } = toolProps;
        const isSelected = selectedToolCode === tool.code;
        const canOpenToolbarOptions = typeof tool.choices !== 'undefined' && typeof ToolOptionsRenderer !== 'undefined';
        const onSelectToolOption: Tool<ToolOptionsProps>['onSelectToolOption'] = async props => {
          if (toolProps.onSelectToolOption) {
            await toolProps.onSelectToolOption(props);
          }
          if (isV3) {
            setSelectedToolCode(initialSelectedToolCode);
          }
          // if there is a change of active group, close the toolbar options
          if (
            typeof props.referencingOtherGroupCode !== 'undefined' &&
            activeGroup?.code !== props.referencingOtherGroupCode
          ) {
            closeToolbarOptions();
          }
        };

        return {
          tiles: [
            ...acc.tiles,
            {
              ...toolProps,
              iconName: BaseToolbarIconNamesMap[tool.code.toLowerCase() as LookupCodes] ?? ICON_NAMES.Circle, // fallback per default
              isSelected: isSelected,
              isExpanded: areTilesExpanded,
              accessible: true,
              accessibilityLabel: i18n.t('customize:open_menu') + ' ' + i18n.t(tool.titleKey),
              accessibilityRole: 'button',
              onPress: async () => {
                let canBeSelected = true;
                if (onOpenTool) {
                  const { canBeSelected: canBeSelectedFromOnOpen } = await onOpenTool(toolProps);
                  canBeSelected = canBeSelectedFromOnOpen;
                }
                if (canBeSelected) {
                  setSelectedToolCode(toolProps.tool.code);
                  amplitude.trackEvent(AnalyticsEvents.personalize.menuClick, { lookup_code: toolProps.tool.code });
                }
              },
              testID: configurationTestID(tool.code),
              focusId: configurationTestID(tool.code),
              focusOrder: toolIndex,
              focusGroup: FocusGroups.TOOLBAR_TILES,
              focusable: isVisible,
              onSelectToolOption,
            },
          ],
          options: canOpenToolbarOptions
            ? [
                ...acc.options,
                {
                  ...toolProps,
                  ToolOptionsRenderer,
                  isActive: isSelected,
                  onBackPress: async () => {
                    closeToolbarOptions();
                    if (onCloseTool) {
                      await onCloseTool(toolProps);
                    }
                  },
                  onSelectToolOption,
                },
              ]
            : acc.options,
        };
      },
      { tiles: [], options: [] },
    );
  }, [tools, areTilesExpanded, selectedToolCode, activeGroup, isVisible]);

  const onDuplicate = () => {
    amplitude.trackEvent(AnalyticsEvents.personalize.duplicatePhotoClick);
    if (activeGroup?.selectedProducts && activeGroup?.selectedProducts?.length > 0 && activeProject) {
      Promise.allSettled(activeGroup.selectedProducts.map(activeProject.duplicateProduct)).then(res => {
        const successCount = res.filter(r => r.status === 'fulfilled').length;
        if (successCount > 0) {
          showOnDuplicateToast({
            message: i18n.t('toast:duplicate', { count: successCount }),
            id: 'toast-duplicate',
          });
        }

        const errorCount = res.filter(r => r.status === 'rejected').length;
        if (errorCount > 0) {
          logError('Error duplicating products', res);
        }
      });
    }
  };

  const onDelete = () => {
    amplitude.trackEvent(AnalyticsEvents.personalize.selectionDeleteClick, {
      n_photo: activeGroup?.selectedProducts.length,
    });
    openModal({
      dynamicModalView: DYNAMIC_MODAL_VIEWS.DeleteAll,
    });
  };

  return {
    isVisible,
    areTilesExpanded,
    expandTiles: () => {
      tilesExpandAnimationEnabled && setAreTilesExpanded(true);
    },
    collapseTiles: () => {
      tilesExpandAnimationEnabled && setAreTilesExpanded(false);
    },
    tiles,
    options,
    isToolbarOptionsOpened,
    selectedToolCode,
    setTutorialArea,
    onClose: closeToolbarOptions,
    onCloseToolbar: closeToolbar,
    onDuplicate,
    onDelete,
  };
};
